import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon";
import logoSmall from "assets/images/sbi/logo_small.svg";
import colors, { SBI_DARK, SBI_LIGHT } from "assets/theme/base/colors";
import { useMaterialUIController } from "context";
import { FRESENIUS } from "contextBranding";

const lessons = [
  { id: 1, title: "Introduction", subtitle: "Get started with EEG basics", time: 8, status: "done", progress: 100 },
  { id: 2, title: "Numeric Rating Scales (NRS)", subtitle: "Understanding NRS assessment", time: 3, status: "done", progress: 100 },
  { id: 3, title: "Month Of The Year Backwards (MOTYB)", subtitle: "MOTYB assessment techniques", time: 5, status: "continue", progress: 60 },
  { id: 4, title: "NuDESC", subtitle: "Nu-DESC assessment protocol", time: 15, status: "start", progress: 0 },
  { id: 5, title: "EEG Signals", subtitle: "EEG signal interpretation", time: 5, status: "start", progress: 0 },
  { id: 6, title: "Exam", subtitle: "Final assessment", time: 40, status: "start", progress: 0 },
];

function ContinueBootcampCard({ bootcamp, isFreseniusBranding = false }) {
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const isLight = controller?.sbiDashboardTheme === "light";
  const scrollContainerRef = useRef(null);
  const firstUndoneLessonRef = useRef(null);
  const sb = colors.backgroundSbi;
  const themeColors = isLight ? SBI_LIGHT : SBI_DARK;
  const freseniusTheme = isLight ? FRESENIUS.themeLight : FRESENIUS.themeDark;
  const cardBg = isFreseniusBranding
    ? (isLight ? freseniusTheme.bgHeader : `${FRESENIUS.blueDark}ee`)
    : (isLight ? themeColors.bgHeader : `${sb.default}99`);
  const cardBorder = isFreseniusBranding
    ? (isLight ? FRESENIUS.blue : freseniusTheme.border)
    : (isLight ? themeColors.border : `${sb.default}80`);

  // Auto-scroll to first undone course on mount
  useEffect(() => {
    if (firstUndoneLessonRef.current && scrollContainerRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        firstUndoneLessonRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, []);

  const handleLessonClick = (lesson) => {
    if (lesson.status !== "done") {
      // Navigate to course page with course name as URL parameter
      const courseName = encodeURIComponent(lesson.title);
      navigate(`/e-learning/course/${courseName}`);
    }
  };

  // Calculate progress
  const courses = lessons.filter(l => l.title !== "Exam");
  const finishedCourses = courses.filter(l => l.status === "done").length;
  const totalCourses = courses.length;
  const exam = lessons.find(l => l.title === "Exam");
  const examMissing = exam && exam.status !== "done";

  return (
    <Card
      sx={(theme) => ({
        height: "610px", // Fixed height
        borderRadius: theme.borders.borderRadius.lg,
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        backgroundColor: cardBg,
        border: `1px solid ${cardBorder}`,
        backdropFilter: "blur(12px)",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 10px 36px rgba(0, 0, 0, 0.4)",
        },
      })}
    >
      <MDBox p={3} display="flex" flexDirection="column" height="100%">
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
          <MDTypography
            variant="h5"
            fontWeight="bold"
            color={isFreseniusBranding ? "white" : "dark"}
          >
            {bootcamp?.title || "EEG Bootcamp for Anaesthesia"}
          </MDTypography>
          <MDBox
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={(theme) => ({
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: isFreseniusBranding ? (isLight ? FRESENIUS.white : FRESENIUS.themeDark.bgMain) : theme.palette.greySbi[100],
              border: `1px solid ${isFreseniusBranding ? (isLight ? FRESENIUS.themeLight.border : FRESENIUS.themeDark.border) : theme.palette.greySbi[300]}`,
            })}
          >
            <MDTypography
              variant="caption"
              fontWeight="medium"
              color={isFreseniusBranding ? "white" : "text"}
            >
              {finishedCourses} / {totalCourses} courses finished
            </MDTypography>
            {examMissing && (
              <>
                <MDTypography
                  variant="caption"
                  color={isFreseniusBranding ? "white" : "text"}
                  sx={{ mx: 0.25 }}
                >
                  •
                </MDTypography>
                <MDTypography
                  variant="caption"
                  fontWeight="medium"
                  color={isFreseniusBranding ? "white" : "text"}
                >
                  1 exam missing
                </MDTypography>
              </>
            )}
          </MDBox>
        </MDBox>

        {/* Lessons List - Scrollable */}
        <MDBox 
          ref={scrollContainerRef}
          display="flex" 
          flexDirection="column" 
          gap={2}
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            pr: 1,
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "rgba(0, 0, 0, 0.05)",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0, 0, 0, 0.2)",
              borderRadius: "3px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.3)",
              },
            },
          }}
        >
          {lessons.map((lesson, index) => {
            // Find first undone lesson
            const isFirstUndone = lesson.status !== "done" && 
              lessons.slice(0, index).every(l => l.status === "done");
            
            return (
              <MDBox
              key={lesson.id}
              ref={isFirstUndone ? firstUndoneLessonRef : null}
              display="flex"
              alignItems="center"
              gap={2}
              sx={(theme) => ({
                p: 3, // Increased padding for taller rows
                minHeight: 100, // Minimum row height
                borderRadius: 2,
                backgroundColor: lesson.status === "done"
                  ? (isFreseniusBranding
                    ? `${FRESENIUS.blueLight}15`
                    : `${theme.palette.statusSbi.success}15`)
                  : "rgba(0, 0, 0, 0.02)",
                border: lesson.status === "done"
                  ? (isFreseniusBranding
                    ? `1px solid ${FRESENIUS.blueLight}66`
                    : `1px solid ${theme.palette.statusSbi.success}40`)
                  : (isFreseniusBranding
                    ? `1px solid ${FRESENIUS.border}`
                    : `1px solid ${theme.palette.borderSbi.subtle}`),
                transition: lesson.status === "done" 
                  ? "none" 
                  : "background-color 0.2s ease, border-color 0.2s ease",
                ...(lesson.status !== "done" && {
                  "&:hover": {
                    backgroundColor: isFreseniusBranding ? `${FRESENIUS.blueLight}15` : "rgba(0, 0, 0, 0.04)",
                    borderColor: isFreseniusBranding ? FRESENIUS.border : theme.palette.borderSbi.strong,
                  },
                }),
              })}
            >
              {/* Progress Circle with Logo */}
              <MDBox position="relative" display="inline-flex" sx={{ flexShrink: 0 }}>
                {/* Outer progress bar */}
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={64}
                  thickness={4}
                  sx={{
                    color: "#e3e6ea",
                    position: "absolute",
                  }}
                />
                {/* Progress circle */}
                <CircularProgress
                  variant="determinate"
                  value={lesson.progress}
                  size={64}
                  thickness={4}
                  sx={(theme) => ({
                    color: lesson.status === "done"
                      ? (isFreseniusBranding ? FRESENIUS.blueLight : theme.palette.statusSbi.success)
                      : (isFreseniusBranding ? FRESENIUS.blue : theme.palette.backgroundSbi.default),
                    "& .MuiCircularProgress-circle": {
                      strokeLinecap: "round",
                    },
                  })}
                />
                {/* Inner circle with background color */}
                <MDBox
                  position="absolute"
                  top={0}
                  left={0}
                  bottom={0}
                  right={0}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={(theme) => ({
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    backgroundColor: lesson.status === "done"
                      ? (isFreseniusBranding ? FRESENIUS.blueLight : theme.palette.statusSbi.success)
                      : (isFreseniusBranding ? FRESENIUS.blue : theme.palette.backgroundSbi.default),
                    margin: "auto",
                  })}
                >
                  {/* Logo inside - white for done, secondary color for others */}
                  <MDBox
                    sx={(theme) => ({
                      width: 32,
                      height: 32,
                      backgroundColor: lesson.status === "done"
                        ? "white"
                        : (isFreseniusBranding ? "white" : theme.palette.secondarySbi.main),
                      mask: `url(${logoSmall}) no-repeat center`,
                      maskSize: "contain",
                      WebkitMask: `url(${logoSmall}) no-repeat center`,
                      WebkitMaskSize: "contain",
                    })}
                    aria-label="SBI Logo"
                  />
                </MDBox>
              </MDBox>

              {/* Title and Subtitle */}
              <MDBox flex={1} minWidth={0}>
                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  color={isFreseniusBranding ? "white" : "dark"}
                  mb={0.5}
                >
                  {lesson.title}
                </MDTypography>
                <MDTypography
                  variant="body2"
                  color={isFreseniusBranding ? "white" : "text"}
                >
                  {lesson.subtitle}
                </MDTypography>
              </MDBox>

              {/* Estimated Time */}
              <MDBox display="flex" alignItems="center" gap={0.75} sx={{ flexShrink: 0 }}>
                <MDBox position="relative" display="inline-flex" sx={{ width: 24, height: 24 }}>
                  {/* Background circle (light gray) */}
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={24}
                    thickness={3}
                    sx={{
                      color: "#e3e6ea",
                      position: "absolute",
                    }}
                  />
                  {/* Fill circle based on estimated time (percentage of 60 minutes) */}
                  <CircularProgress
                    variant="determinate"
                    value={(lesson.time / 60) * 100}
                    size={24}
                    thickness={3}
                    sx={(theme) => ({
                      color: isFreseniusBranding ? FRESENIUS.blueLight : theme.palette.accentSbi.cyan,
                      position: "absolute",
                      "& .MuiCircularProgress-circle": {
                        strokeLinecap: "round",
                      },
                    })}
                  />
                </MDBox>
                <MDTypography
                  variant="caption"
                  color={isFreseniusBranding ? "white" : "text"}
                  fontWeight="medium"
                >
                  {lesson.time}min
                </MDTypography>
              </MDBox>

              {/* Action Button */}
              <IconButton
                onClick={() => handleLessonClick(lesson)}
                disabled={lesson.status === "done"}
                sx={(theme) => ({
                  flexShrink: 0,
                  background: lesson.status === "done"
                    ? `linear-gradient(135deg, ${theme.palette.greySbi[500]}80 0%, ${theme.palette.greySbi[600]}80 100%)`
                    : isFreseniusBranding
                    ? `linear-gradient(135deg, ${FRESENIUS.blue} 0%, ${FRESENIUS.blueLight} 100%)`
                    : `linear-gradient(135deg, ${theme.palette.primarySbi.main} 0%, ${theme.palette.primarySbi.focus} 100%)`,
                  color: lesson.status === "done" ? theme.palette.greySbi[300] : "white",
                  width: 40,
                  height: 40,
                  cursor: lesson.status === "done" ? "default" : "pointer",
                  opacity: lesson.status === "done" ? 0.6 : 1,
                  "&:hover": {
                    background: lesson.status === "done"
                      ? `linear-gradient(135deg, ${theme.palette.greySbi[500]}80 0%, ${theme.palette.greySbi[600]}80 100%)`
                      : isFreseniusBranding
                      ? `linear-gradient(135deg, ${FRESENIUS.blueLight} 0%, ${FRESENIUS.blue} 100%)`
                      : `linear-gradient(135deg, ${theme.palette.primarySbi.focus} 0%, ${theme.palette.primarySbi.main} 100%)`,
                  },
                  "&.Mui-disabled": {
                    background: `linear-gradient(135deg, ${theme.palette.greySbi[500]}80 0%, ${theme.palette.greySbi[600]}80 100%)`,
                    color: theme.palette.greySbi[300],
                  },
                })}
              >
                {lesson.status === "done" ? (
                  <Icon sx={{ fontSize: "1.5rem" }}>check</Icon>
                ) : (
                  <Icon sx={{ fontSize: "1.5rem" }}>play_arrow</Icon>
                )}
              </IconButton>
            </MDBox>
            );
          })}
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default ContinueBootcampCard;
